import React, { useEffect, useState } from 'react'
import styles from './index.module.css'
import Header from '../components/header'
import RoundedButton from '../components/roundedButton'
import RoundedInput from '../components/roundedInput'
import { Delete, Photo, Search, Videocam } from '@mui/icons-material'
import { Modal } from '@mui/material'
import NewPropertyModal from '../components/newPropertyModal'
import ApiService from '../ApiService'
import { PropertyResponse, UserResponse } from '../models'
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom'
import Loading from '../components/loading'
import DeleteModal from '../components/deleteModal'

export default function Imoveis() {
  const navigate = useNavigate()
  const [userData, setUserData] = useState<UserResponse>()
  const [newPropertyModal, setNewPropertyModal] = useState<boolean>(false)
  const [properties, setProperties] = useState<PropertyResponse[]>([])
  const [selectedProperty, setSelectedProperty] = useState<PropertyResponse | undefined>()
  const [page, setPage] = useState<number>(1)
  const [lastFetched, setLastFetched] = useState<number>()
  const [fetching, setFetching] = useState<boolean>(false)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [openDeleteModal, setOpenDeleteModal] = useState<PropertyResponse | undefined>()
  const landlordList: UserResponse[] = []
  const tenantList: UserResponse[] = []
  const canManageProperties = userData?.account_type === 'admin' || userData?.account_type === 'IMOBILIARIA'
  const propertySideLabel = userData?.account_type === 'PROPRIETARIO' ? 'Inquilino' : 'Proprietário'
  const showPropertySideColumn = canManageProperties || userData?.account_type === 'PROPRIETARIO'
  const tableColumns = [
    { key: 'code', label: 'Cód. Imóvel', className: styles.codeColumn },
    ...(showPropertySideColumn ? [{ key: 'relatedUser', label: propertySideLabel, className: styles.ownerColumn }] : []),
    { key: 'address', label: 'Endereço', className: styles.addressColumn },
    { key: 'cep', label: 'CEP', className: styles.cepColumn },
    { key: 'neighborhood', label: 'Bairro', className: styles.neighborhoodColumn },
    { key: 'city', label: 'Cidade', className: styles.cityColumn },
    { key: 'media', label: 'Mídia', className: styles.mediaColumn },
    ...(canManageProperties ? [{ key: 'actions', label: 'Ações', className: styles.actionsColumn }] : []),
  ]

  const handleDeleteProperty = async (property: PropertyResponse | undefined) => {
    try {
        await ApiService.deleteProperty(property?.id?.toString() ?? '')
        await refreshProperties()
    }
    catch (error: any) {
        console.log(error)
    }
}

  const handleSearch = async (term: string) => {
    const trimmedSearchTerm = term.trim();
    const encodedSearchTerm = encodeURIComponent(trimmedSearchTerm);
    setSearchTerm(trimmedSearchTerm)
  
    try {
      if (trimmedSearchTerm.length >= 4) {
        const response = await ApiService.getPropertyBySearchTerm(encodedSearchTerm)
        const data = response.data as PropertyResponse[]
        setProperties(data);
        setPage(1)
        setLastFetched(data.length)
        return
      }
      if (!trimmedSearchTerm) {
        setPage(1)
        await getPropertiesList(1)
      }
    } catch (error:any) {
      if (error?.name === 'AbortError') {
        // Ignore the request cancellation error
      } else {
        console.error('Error searching users:', error);
      }
    }
  }



  const getPropertiesList = async (page:number) => {
    setIsLoading(true)
    try{
      const data:PropertyResponse[] = await ApiService.getProperty('all',page) as PropertyResponse[]
      setProperties((currentProperties) => page !== 1 ? [...currentProperties, ...data] : data)
      setLastFetched(data.length)
    }
    catch (error: any) {
      console.log(error)
    }
    finally{
      setIsLoading(false)
    }
  }

  const refreshProperties = async () => {
    if (searchTerm && searchTerm.length >= 4) {
      await handleSearch(searchTerm)
      return
    }

    setPage(1)
    await getPropertiesList(1)
  }

  const handleScroll = async (e:any) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    const bufferHeight = 50;
    // Check if the user has scrolled to the bottom
    if (
        Math.floor(scrollTop + clientHeight) >= Math.floor(scrollHeight) - bufferHeight && 
        lastFetched && lastFetched >= 100 && 
        !fetching &&
        searchTerm === ''
      ) {
      setFetching(true)
      await getPropertiesList(page+1);
      setPage(page => page+1)
      setFetching(false)
    }
  };

  /*const handleGetLandlordList = async () => {
    const landlord = await ApiService.getUserByType('PROPRIETARIO')
    setLandlordList(landlord)
  }

  const handleGetTenantList = async () => {
    const tenant = await ApiService.getUserByType('INQUILINO')
    setTenantList(tenant)
  }*/

  useEffect(() => {
    getPropertiesList(1)
    //handleGetLandlordList()
    //handleGetTenantList()
    const storedUser = localStorage.getItem('userData')
    if (!storedUser) {
      navigate('/login')
      return
    }

    const user = JSON.parse(storedUser) as UserResponse
    setUserData(user)
    
  },[navigate])

  return (
    <div className={styles.background}>
        <Modal open={newPropertyModal} onClose={() => setNewPropertyModal(false)}>
          <NewPropertyModal 
            closeCallback={() => setNewPropertyModal(false)} 
            propertyToEdit={selectedProperty}
            onSuccessCallback={refreshProperties}
          />
        </Modal>
        <Modal open={openDeleteModal? true : false} onClose={() => setOpenDeleteModal(undefined)}>
          <DeleteModal resourceToDelete={openDeleteModal?.id ?? 0} onCloseCallback={() => setOpenDeleteModal(undefined)} onAcceptCallback={() => handleDeleteProperty(openDeleteModal)}/>
        </Modal>
        <Header />
        <p className={styles.pageTitle}>Lista de Imóveis</p>
        <div className={styles.menuRow}>
          {(userData?.account_type === 'admin' || userData?.account_type === 'IMOBILIARIA') &&
            <div className={styles.newPropertyButton}>
              <RoundedButton buttonText='Novo Imóvel' buttonType='button' onClickCallback={() => {setNewPropertyModal(true); setSelectedProperty(undefined)}}/>
            </div>
          }
          <div className={styles.searchPropertyInput}>
            <Search />
            <RoundedInput transparent disableLabel label='Pesquisar Imóvel' type='text' onChangeCallback={handleSearch} />
          </div>
        </div>
        <div className={styles.tableShell}>
          <div className={styles.tableViewport} onScroll={handleScroll}>
            <table className={styles.table}>
              <thead className={styles.tableHead}>
                <tr className={styles.headerRow}>
                  {tableColumns.map((column) => (
                    <th key={column.key} className={column.className}>
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className={styles.tableBody}>
                {properties.map((property) => (
                  <tr key={property.id} className={styles.dataRow} onClick={() => navigate('/imovel',{state: {property: property, tenantList: tenantList, landlordList: landlordList}})}>
                    <td className={styles.codeColumn} data-label='Cód. Imóvel'>
                      <span className={styles.codeBadge}>{property.code}</span>
                    </td>
                    {showPropertySideColumn && (
                      <td className={styles.ownerColumn} data-label={propertySideLabel}>
                        <span className={styles.primaryText}>
                          {canManageProperties ? property.landlord_name : property.tenant_name}
                        </span>
                      </td>
                    )}
                    <td className={styles.addressColumn} data-label='Endereço'>
                      <div className={styles.addressContent}>
                        <span className={styles.primaryText}>{`${property.address} ${property.address_no}`}</span>
                        {property.address_l2 && (
                          <span className={styles.secondaryText}>{property.address_l2}</span>
                        )}
                      </div>
                    </td>
                    <td className={styles.cepColumn} data-label='CEP'>
                      <span className={styles.primaryText}>{property.cep}</span>
                    </td>
                    <td className={styles.neighborhoodColumn} data-label='Bairro'>
                      <span className={styles.primaryText}>{property.address_neighborhood}</span>
                    </td>
                    <td className={styles.cityColumn} data-label='Cidade'>
                      <div className={styles.addressContent}>
                        <span className={styles.primaryText}>{property.city}</span>
                        <span className={styles.secondaryText}>{property.state}</span>
                      </div>
                    </td>
                    <td className={styles.mediaColumn} data-label='Mídia'>
                      <button
                        type='button'
                        className={styles.mediaStatusButton}
                      >
                        <span className={`${styles.mediaBadge} ${property.num_images > 0 ? styles.mediaBadgeAvailable : styles.mediaBadgeMuted}`}>
                          <Photo fontSize='small' />
                          <span>{property.num_images > 0 ? `${property.num_images} ${property.num_images > 1 ? 'imagens' : 'imagem'}` : 'Sem imagens'}</span>
                        </span>
                        <span className={`${styles.mediaBadge} ${property.num_videos > 0 ? styles.mediaBadgeAvailable : styles.mediaBadgeMuted}`}>
                          <Videocam fontSize='small' />
                          <span>{property.num_videos > 0 ? `${property.num_videos} vídeo${property.num_videos > 1 ? 's' : ''}` : 'Sem vídeos'}</span>
                        </span>
                      </button>
                    </td>
                    {canManageProperties && (
                      <td className={styles.actionsColumn} data-label='Ações'>
                        <div className={styles.actionsContainer}>
                          <button
                            type='button'
                            className={styles.actionButton}
                            onClick={(e) => {e?.preventDefault(); e?.stopPropagation();setSelectedProperty(property); setNewPropertyModal(true)}}
                          >
                            <EditIcon fontSize='small' />
                            <span>Editar</span>
                          </button>
                          <button
                            type='button'
                            className={`${styles.actionButton} ${styles.deleteButton}`}
                            onClick={(e) => {e?.preventDefault(); e?.stopPropagation();setOpenDeleteModal(property)}}
                          >
                            <Delete fontSize='small' />
                            <span>Excluir</span>
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {isLoading && (
              <div className={styles.loadingContainer}>
                <Loading />
              </div>
            )}
            {!isLoading && properties.length === 0 && (
              <div className={styles.emptyState}>
                Nenhum imóvel encontrado para os filtros atuais.
              </div>
            )}
          </div>
        </div>
    </div>
  )
}
